import { useRef, useState, useCallback, useEffect } from 'react';

// Pentatonic scale frequencies (Hz) - always sounds pleasant
const XYLOPHONE_NOTES = {
  C5: 523.25,
  D5: 587.33,
  E5: 659.25,
  G5: 783.99,
  A5: 880.0,
  C6: 1046.5,
} as const;

export type NoteKey = keyof typeof XYLOPHONE_NOTES;

const STORAGE_KEY = 'fluxconn-sound';
const DEBOUNCE_MS = 100;

export function useSound() {
  const audioContextRef = useRef<AudioContext | null>(null);
  const lastPlayedRef = useRef<number>(0);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Load saved preference
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'muted') {
      setSoundEnabled(false);
    }
  }, []);

  // Initialize audio context on first interaction
  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext ||
        (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  // Play xylophone note with realistic envelope
  const playNote = useCallback(
    (noteKey: NoteKey) => {
      if (!soundEnabled) return;

      // Check for reduced motion preference
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      // Debounce: prevent sound spam (100ms minimum gap)
      const now = Date.now();
      if (now - lastPlayedRef.current < DEBOUNCE_MS) return;
      lastPlayedRef.current = now;

      const audioCtx = initAudio();
      if (audioCtx.state === 'suspended') {
        audioCtx.resume();
      }

      const frequency = XYLOPHONE_NOTES[noteKey];
      const duration = 0.4;
      const currentTime = audioCtx.currentTime;

      // Create oscillators for richer xylophone tone
      const fundamental = audioCtx.createOscillator();
      const harmonic = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      const harmonicGain = audioCtx.createGain();

      // Fundamental tone
      fundamental.type = 'sine';
      fundamental.frequency.value = frequency;

      // Add slight harmonic for metallic quality
      harmonic.type = 'sine';
      harmonic.frequency.value = frequency * 3; // 3rd harmonic
      harmonicGain.gain.value = 0.1; // Subtle

      // Xylophone envelope: fast attack, medium decay
      gainNode.gain.setValueAtTime(0, currentTime);
      gainNode.gain.linearRampToValueAtTime(0.25, currentTime + 0.008); // 8ms attack
      gainNode.gain.exponentialRampToValueAtTime(0.001, currentTime + duration);

      // Connect nodes
      fundamental.connect(gainNode);
      harmonic.connect(harmonicGain);
      harmonicGain.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      // Play
      fundamental.start(currentTime);
      harmonic.start(currentTime);
      fundamental.stop(currentTime + duration);
      harmonic.stop(currentTime + duration);
    },
    [soundEnabled, initAudio]
  );

  // Toggle sound on/off
  const toggleSound = useCallback(() => {
    setSoundEnabled((prev) => {
      const newValue = !prev;
      localStorage.setItem(STORAGE_KEY, newValue ? 'enabled' : 'muted');
      if (newValue) {
        // Play a note when enabling to confirm
        setTimeout(() => playNote('C5'), 50);
      }
      return newValue;
    });
  }, [playNote]);

  return {
    soundEnabled,
    toggleSound,
    playNote,
    notes: XYLOPHONE_NOTES,
  };
}

// Predefined note sequences for nav items (ascending left to right)
export const NAV_NOTES: NoteKey[] = ['C5', 'D5', 'E5', 'G5', 'A5', 'C6'];
